import { AlarmClock, AtSign, BellOff, Calendar, CheckCheck, CornerUpLeft, Shield, UserCheck, UserPlus } from '@/components/ui/icons';
import { useEffect, type RefObject } from 'react';
import { useNavigate } from 'react-router';
import type { NotificationDTO } from '@clanfire/shared';
import { notificationApi } from '@/api/endpoints';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { EmptyState, ErrorState } from '@/components/ui/Feedback';
import { Popover } from '@/components/ui/Popover';
import { Spinner } from '@/components/ui/Spinner';
import { formatRelative } from '@/lib/time';
import { cn } from '@/lib/utils';
import { targetKey } from '@/stores/messages';
import { useNotifications } from '@/stores/social';
import { toast, useUi } from '@/stores/ui';

const icons = {
  MENTION: <AtSign size={14} />,
  REPLY: <CornerUpLeft size={14} />,
  FRIEND_REQUEST: <UserPlus size={14} />,
  FRIEND_ACCEPTED: <UserCheck size={14} />,
  EVENT_REMINDER: <Calendar size={14} />,
  RAID_ALERT: <Shield size={14} />,
  REMINDER: <AlarmClock size={14} />,
};

function describe(n: NotificationDTO) {
  const who = n.actor?.displayName ?? 'Someone';
  switch (n.type) {
    case 'MENTION':
      return <><strong>{who}</strong> mentioned you in <strong>#{n.channelName}</strong>{n.serverName && <span className="text-fg-muted"> · {n.serverName}</span>}</>;
    case 'REPLY':
      return <><strong>{who}</strong> replied to you in <strong>#{n.channelName}</strong>{n.serverName && <span className="text-fg-muted"> · {n.serverName}</span>}</>;
    case 'FRIEND_REQUEST':
      return <><strong>{who}</strong> sent you a friend request</>;
    case 'FRIEND_ACCEPTED':
      return <><strong>{who}</strong> accepted your friend request</>;
    case 'EVENT_REMINDER':
      return <><strong>{n.event?.name ?? 'An event'}</strong> starts soon{n.serverName && <span className="text-fg-muted"> · {n.serverName}</span>}</>;
    case 'REMINDER':
      return (
        <>
          <strong>Reminder:</strong> a message from <strong>{who}</strong>
          {n.channelName ? <> in <strong>#{n.channelName}</strong></> : null}
          {n.serverName && <span className="text-fg-muted"> · {n.serverName}</span>}
        </>
      );
    case 'RAID_ALERT':
      return <>Lots of people joined <strong>{n.serverName ?? 'your server'}</strong> at once, so its invites are paused for 30 minutes. Tap to check.</>;
  }
}

export function NotificationInbox({ open, onClose, anchor }: { open: boolean; onClose: () => void; anchor: RefObject<HTMLElement | null> }) {
  const { status, items, unreadCount, hasMore } = useNotifications();
  const navigate = useNavigate();
  const requestJump = useUi((s) => s.requestJump);

  useEffect(() => {
    if (open) void useNotifications.getState().load();
  }, [open]);

  const markAll = async () => {
    useNotifications.getState().markRead('all');
    await notificationApi.markAllRead().catch(() => toast.error('Could not mark notifications as read.'));
  };

  const openNotification = (n: NotificationDTO) => {
    if (!n.readAt) {
      useNotifications.getState().markRead([n.id]);
      void notificationApi.markRead([n.id]).catch(() => undefined);
    }
    onClose();
    if (n.type === 'RAID_ALERT' && n.serverId) {
      navigate(`/app/${n.serverId}`);
      useUi.getState().openModal({ type: 'serverSettings', serverId: n.serverId, section: 'safety' });
    } else if (n.type === 'EVENT_REMINDER' && n.serverId) {
      navigate(`/app/${n.serverId}`);
      useUi.getState().openModal({ type: 'events', serverId: n.serverId, eventId: n.event?.id });
    } else if (n.conversationId) {
      navigate(`/app/@me/${n.conversationId}`);
      if (n.messageId) requestJump(targetKey({ conversationId: n.conversationId }), n.messageId);
    } else if (n.channelId && n.serverId) {
      navigate(`/app/${n.serverId}/${n.channelId}`);
      if (n.messageId) requestJump(targetKey({ channelId: n.channelId }), n.messageId);
    } else {
      navigate('/app/@me');
    }
  };

  return (
    <Popover open={open} onClose={onClose} anchor={anchor} placement="bottom-end" label="Notifications" className="flex w-[min(420px,calc(100vw-16px))] flex-col">
      <div className="flex items-center justify-between border-b border-line px-4 py-3">
        <h2 className="font-bold">Inbox {unreadCount > 0 && <span className="text-sm font-semibold text-fg-muted">· {unreadCount} unread</span>}</h2>
        <Button variant="ghost" size="sm" icon={<CheckCheck size={15} />} onClick={() => void markAll()} disabled={unreadCount === 0}>
          Mark all read
        </Button>
      </div>
      <div className="max-h-[min(520px,70dvh)] overflow-y-auto p-1.5">
        {status === 'loading' && items.length === 0 ? (
          <div className="flex justify-center py-10 text-fg-muted">
            <Spinner />
          </div>
        ) : status === 'error' ? (
          <ErrorState message="Could not load notifications." onRetry={() => void useNotifications.getState().load()} />
        ) : items.length === 0 ? (
          <EmptyState icon={<BellOff size={26} />} title="You're all caught up" body="Mentions, replies and friend requests will show up here." />
        ) : (
          <ul>
            {items.map((n) => (
              <li key={n.id}>
                <button
                  type="button"
                  onClick={() => openNotification(n)}
                  className={cn('flex w-full gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-hover', !n.readAt && 'bg-accent-soft/60')}
                >
                  <span className="relative shrink-0">
                    <Avatar user={n.actor} size={36} />
                    <span className="absolute -right-1 -bottom-1 flex h-5 w-5 items-center justify-center rounded-full bg-accent text-accent-fg ring-2 ring-elevated">{icons[n.type]}</span>
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm leading-snug text-fg-soft">{describe(n)}</span>
                    {n.preview && <span className="mt-0.5 line-clamp-2 block text-sm text-fg-muted">{n.preview}</span>}
                    <span className="mt-0.5 block text-xs text-fg-muted">{formatRelative(n.createdAt)}</span>
                  </span>
                  {!n.readAt && <span aria-label="Unread" className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-accent" />}
                </button>
              </li>
            ))}
            {hasMore && (
              <li className="p-2">
                <Button variant="secondary" size="sm" block onClick={() => void useNotifications.getState().loadMore()}>
                  Load more
                </Button>
              </li>
            )}
          </ul>
        )}
      </div>
    </Popover>
  );
}
